import { useEffect, useRef, useState } from "react";
import {
  formatDob,
  getAllowedMonthsForDay,
  getAllowedYearsForDayMonth,
  getInitialDayOptions,
  parseDob,
  MONTH_LABELS,
} from "../../utils/dob";

type Segment = "day" | "month" | "year";

interface DateOfBirthPickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  titleColor?: string;
  backgroundColor?: string;
}

const CalendarIcon = () => (
  <svg
    className="w-4 h-4 text-text-subtle shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

function DateOfBirthPicker({
  value,
  onChange,
  disabled = false,
  titleColor = "text-text-subtle",
  backgroundColor = "bg-bg-page-body",
}: DateOfBirthPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [openSegment, setOpenSegment] = useState<Segment | null>(null);

  const parsed = value ? parseDob(value) : null;
  const [day, setDay] = useState<number | null>(parsed?.day ?? null);
  const [month, setMonth] = useState<number | null>(parsed?.month ?? null);
  const [year, setYear] = useState<number | null>(parsed?.year ?? null);

  useEffect(() => {
    const next = value ? parseDob(value) : null;
    setDay(next?.day ?? null);
    setMonth(next?.month ?? null);
    setYear(next?.year ?? null);
  }, [value]);

  useEffect(() => {
    if (disabled) setOpenSegment(null);
  }, [disabled]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpenSegment(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const emitChange = (
    nextDay: number | null,
    nextMonth: number | null,
    nextYear: number | null,
  ) => {
    if (nextDay && nextMonth && nextYear) {
      onChange(formatDob(nextYear, nextMonth, nextDay));
      return;
    }
    onChange("");
  };

  const getCurrentStep = (): Segment => {
    if (openSegment) return openSegment;
    if (!day) return "day";
    if (!month) return "month";
    return "year";
  };

  const openOnly = (segment: Segment) => {
    if (disabled) return;
    if (segment === "month" && !day) return;
    if (segment === "year" && (!day || !month)) return;
    setOpenSegment((current) => (current === segment ? null : segment));
  };

  const openCurrentStep = () => openOnly(getCurrentStep());

  const handleDaySelect = (d: number) => {
    let nextMonth = month;
    let nextYear = year;

    if (nextMonth && !getAllowedMonthsForDay(d).includes(nextMonth)) {
      nextMonth = null;
      nextYear = null;
    } else if (nextMonth && nextYear) {
      const allowedYears = getAllowedYearsForDayMonth(d, nextMonth);
      if (!allowedYears.includes(nextYear)) nextYear = null;
    }

    setDay(d);
    setMonth(nextMonth);
    setYear(nextYear);
    setOpenSegment(null);
    emitChange(d, nextMonth, nextYear);
  };

  const handleMonthSelect = (m: number) => {
    if (!day) return;

    let nextYear = year;
    if (nextYear) {
      const allowedYears = getAllowedYearsForDayMonth(day, m);
      if (!allowedYears.includes(nextYear)) nextYear = null;
    }

    setMonth(m);
    setYear(nextYear);
    setOpenSegment(null);
    emitChange(day, m, nextYear);
  };

  const handleYearSelect = (y: number) => {
    if (!day || !month) return;
    setYear(y);
    setOpenSegment(null);
    emitChange(day, month, y);
  };

  const dayOptions = getInitialDayOptions();
  const monthOptions = day ? getAllowedMonthsForDay(day) : [];
  const yearOptions =
    day && month ? getAllowedYearsForDayMonth(day, month) : [];

  const dayText = day ? String(day).padStart(2, "0") : "dd";
  const monthText = month ? String(month).padStart(2, "0") : "mm";
  const yearText = year ? String(year) : "yyyy";

  const isSegmentSelected = (segment: Segment, optionValue: number) => {
    if (segment === "day") return day === optionValue;
    if (segment === "month") return month === optionValue;
    return year === optionValue;
  };

  const segmentClass = (segment: Segment, filled: boolean) => {
    const isFlowDisabled =
      (segment === "month" && !day) || (segment === "year" && (!day || !month));

    return [
      "bg-transparent border-0 p-0 text-[12px] font-normal focus:outline-none",
      filled ? "text-text-base" : "text-border-input-dark-bg",
      disabled
        ? ""
        : isFlowDisabled
          ? "cursor-not-allowed opacity-50"
          : "cursor-pointer",
    ].join(" ");
  };

  const dropdownOptions =
    openSegment === "day"
      ? dayOptions.map((d) => ({
          value: d,
          label: String(d).padStart(2, "0"),
        }))
      : openSegment === "month"
        ? monthOptions.map((m) => ({
            value: m,
            label: MONTH_LABELS[m - 1],
          }))
        : openSegment === "year"
          ? yearOptions.map((y) => ({ value: y, label: String(y) }))
          : [];

  const handleOptionSelect = (val: number) => {
    if (openSegment === "day") handleDaySelect(val);
    else if (openSegment === "month") handleMonthSelect(val);
    else if (openSegment === "year") handleYearSelect(val);
  };

  const isOpen = openSegment !== null && dropdownOptions.length > 0;

  return (
    <div className="w-full min-w-0 flex flex-col" ref={containerRef}>
      <span className={`${titleColor} text-[10px] font-normal`}>
        Date of Birth
      </span>
      <div className="my-[3px]" />

      <div className="relative">
        <div
          className={[
            "flex items-center justify-between gap-1 w-full px-2 py-[8px] border border-border-subtle rounded-lg",
            disabled
              ? "bg-bg-disabled opacity-70 pointer-events-none cursor-default"
              : `${backgroundColor} cursor-pointer`,
          ].join(" ")}
          aria-disabled={disabled}
        >
          <div className="flex items-center min-w-0 select-none">
            <button
              type="button"
              className={segmentClass("day", !!day)}
              onClick={() => openOnly("day")}
            >
              {dayText}
            </button>
            <span className="text-border-input-dark-bg mx-0.5 text-[12px]">
              /
            </span>
            <button
              type="button"
              disabled={!day}
              className={segmentClass("month", !!month)}
              onClick={() => openOnly("month")}
            >
              {monthText}
            </button>
            <span className="text-border-input-dark-bg mx-0.5 text-[12px]">
              /
            </span>
            <button
              type="button"
              disabled={!day || !month}
              className={segmentClass("year", !!year)}
              onClick={() => openOnly("year")}
            >
              {yearText}
            </button>
          </div>

          <button
            type="button"
            className="shrink-0 flex items-center cursor-pointer disabled:cursor-not-allowed"
            onClick={openCurrentStep}
            aria-label={`Select ${getCurrentStep()}`}
            disabled={disabled}
          >
            <CalendarIcon />
          </button>
        </div>

        {isOpen && openSegment ? (
          <div className="absolute z-[9999] left-0 right-0 mt-1 overflow-hidden rounded-lg border border-border-subtle bg-bg-page-body shadow-lg">
            <div className="max-h-40 overflow-y-auto scrollbar-translucent py-1">
              {dropdownOptions.map((opt) => {
                const selected = isSegmentSelected(openSegment, opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={[
                      "w-full px-2 py-[6px] text-left text-[12px] font-normal cursor-pointer",
                      selected
                        ? "bg-bg-base text-text-base font-normal"
                        : "text-text-base hover:bg-bg-base",
                    ].join(" ")}
                    onClick={() => handleOptionSelect(opt.value)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default DateOfBirthPicker;
